import { X } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { messageDir } from '../../../../lib/message-direction';
import { TASK_DETAILS_PAGE } from '../../../../lib/tasks-bi';
import { Markdown } from '../../../Markdown';
import { Modal } from '../../../Modal';
import { Badge, Button, OverflowMenu, TaskRowBadge, TextButton } from '../../ui';
import { AgentConversationCard } from '../AgentConversationCard';
import { TaskActivityTimeline } from '../TaskActivityTimeline';
import { TaskFilesSection } from '../TaskFilesSection';
import { TaskSentOutreachSection } from '../TaskSentOutreachSection';
import { TaskChatOriginLine, TaskOriginLine, TaskScheduledLine } from '../TaskOriginLine';
import { TaskDecisionPrompts } from '../TaskDecisionPrompts';
import { TaskBriefSection, TaskNotesSection } from '../TaskUserEdits';
import { useTaskDetailModal } from '../use-task-detail-modal';
import { CancelTaskConfirm } from '../CancelTaskConfirm';
import taskStyles from '../TaskDetailsPanel.module.css';
import styles from '../TaskDetailModal.module.css';

// The wide task-details drill-in: the DS centered <Modal> (Figma "Single Task [2.0]", 20846-93098). The task
// title is the modal header; the body opens on the origin line with the status badge at its end, the
// created date and the description, then the briefing over the plan, the files, the notes and the agent's
// conversation; the footer carries Close and the primary action. Behaviour lives in useTaskDetailModal —
// this file is only its wide look.
export function TaskDetailDesktop({ taskId }: { taskId: string }) {
  const vm = useTaskDetailModal(taskId);
  const { t } = useTranslation();

  if (vm.kind === 'loading') {
    return (
      <Modal open onClose={vm.close} size="medium" backAction={vm.back}>
        <div className={taskStyles.skel}>
          <div className="orion-shimmer" style={{ height: 18, width: '60%' }} />
          <div className="orion-shimmer" style={{ height: 13, width: '90%' }} />
          <div className="orion-shimmer" style={{ height: 13, width: '80%' }} />
        </div>
      </Modal>
    );
  }
  if (vm.kind === 'error') {
    return (
      <Modal open onClose={vm.close} size="medium" title={t('tasks.detail.title')} labelledBy="task-detail-title" headerSeparator backAction={vm.back}>
        <div className={taskStyles.error}>{t('tasks.detail.notFound')}</div>
      </Modal>
    );
  }

  const { task, title, description } = vm;
  const cancelItem = vm.menu.find((item) => item.key === 'cancel');
  return (
    <Modal
      open
      onClose={vm.close}
      size="medium"
      maxHeightPx={650}
      title={title}
      labelledBy="task-detail-title"
      headerSeparator
      backAction={vm.back}
      footerSeparator
      scrollBody
      footer={
        <>
          {/* Cancel Task sits beside Close as a destructive text button (no box), so it reads as quieter
              than the two buttons; it only opens the confirm, never cancels on the spot. Anything else on
              offer (the Maestro handoff a known step displaced) stays in the menu at the inline-start. */}
          {vm.menu.some((item) => item.key !== 'cancel') && (
            <div className={styles.footerMenu}>
              <OverflowMenu
                ariaLabel={t('tasks.rowMenu.label', { title })}
                actions={vm.menu
                  .filter((item) => item.key !== 'cancel')
                  .map((item) => ({
                    key: item.key,
                    label: item.label,
                    destructive: item.destructive,
                    onSelect: item.onClick,
                  }))}
              />
            </div>
          )}
          {cancelItem && (
            <TextButton color="destructive" size="small" startIcon={<X size={14} aria-hidden />} onClick={cancelItem.onClick} className={styles.footerCancel}>
              {cancelItem.label}
            </TextButton>
          )}
          {/* NOT "Close": sitting beside "Resolve with {agent}" that read as closing the TASK. This
              button only dismisses the dialog, and now says so. */}
          <Button variant="outlined" onClick={vm.close}>
            {t('tasks.detail.closeDetails')}
          </Button>
          <Button onClick={vm.cta.onClick}>{vm.cta.label}</Button>
        </>
      }
    >
      <div className={styles.body}>
        <div className={styles.meta}>
          {/* Where this task came from — the goal that laid it out, otherwise the chat it was born in
              (`taskChatOrigin`), never both — with the status badge held to the row's inline-end. */}
          <div className={styles.originRow}>
            <div className={styles.origin}>
              {vm.goalOrigin && <TaskOriginLine origin={vm.goalOrigin} detail />}
              {vm.chatOrigin && <TaskChatOriginLine onOpen={vm.onOpenChatOrigin} />}
            </div>
            {vm.scheduledLabel ? (
              <Badge variant="waiting-on-a-reply" label={vm.scheduledLabel} />
            ) : vm.gating && !vm.awaitingDecision ? (
              <Badge variant="waiting-on-a-reply" label={t('tasks.status.after', { title: vm.gating.title })} />
            ) : (
              <TaskRowBadge task={task} detail />
            )}
          </div>
          {/* WHEN it starts, when that is known: a date of its own, or the task it waits for. */}
          {vm.scheduledWhen && <TaskScheduledLine when={vm.scheduledWhen} />}
          {vm.created && <div className={styles.created}>{t('tasks.detail.created', { date: vm.created })}</div>}
          {description && (
            <div className={styles.desc}>
              <Markdown variant="detail" dir={messageDir(description)}>
                {description}
              </Markdown>
            </div>
          )}
        </div>
        {/* The question this task is held on, answered in place — the drill-in's half of the row's own
            chips. `TASK_DETAILS_PAGE` so a tap here is not misattributed to the Tasks list. */}
        <TaskDecisionPrompts task={task} origin={vm.goalOrigin} onAnsweredChange={vm.onDecisionAnswered} page={TASK_DETAILS_PAGE} />
        <div className={styles.plan}>
          <TaskBriefSection task={task} editable={vm.editable} edits={vm.edits} />
          <TaskActivityTimeline
            agentId={task.agentId}
            agentName={vm.agentName}
            steps={task.steps}
            currentStep={vm.currentStep}
            statusVariant={vm.statusVariant}
          />
        </div>
        <TaskSentOutreachSection outreaches={vm.sentOutreaches} />
        <TaskFilesSection files={vm.files} onOpen={vm.onOpenFile} className={styles.files} />
        {/* The briefing above and the notes here are the drill-in's two writes — one shared module with
            the phone sheet, so the two layouts cannot grow two different forms. */}
        <TaskNotesSection notes={vm.notes ?? []} editable={vm.editable} edits={vm.edits} agentName={vm.agentName} layout="inline" className={styles.notes} />
        <AgentConversationCard taskId={taskId} dashboardBuild={!!task.isDashboardBuild} className={styles.conversation} />
        <CancelTaskConfirm flow={vm.cancel} />
      </div>
    </Modal>
  );
}
